import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileText, ExternalLink, Clock, CheckCircle2, MessageCircle, Send, Activity } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VendorPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [chatBody, setChatBody] = useState("");
  const [timeline, setTimeline] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/vendor/${token}`);
      setData(r.data); setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Link invalid or expired");
    }
    try {
      const m = await axios.get(`${BACKEND_URL}/api/vendor/${token}/messages`);
      setMessages(m.data || []);
    } catch {}
    try {
      const tl = await axios.get(`${BACKEND_URL}/api/vendor/${token}/timeline`);
      setTimeline(tl.data);
    } catch {}
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [token]);

  const upload = async () => {
    if (!file) return toast.error("Choose a file first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("note", note);
      await axios.post(`${BACKEND_URL}/api/vendor/${token}/upload`, fd);
      toast.success("Upload delivered to HCD Legal");
      setFile(null); setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const sendChat = async () => {
    if (!chatBody.trim()) return;
    try {
      await axios.post(`${BACKEND_URL}/api/vendor/${token}/messages`, { body: chatBody });
      setChatBody("");
      load();
    } catch (e) { toast.error("Send failed"); }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="bg-white border border-slate-200 rounded-md p-8 max-w-md text-center">
          <div className="text-red-600 font-bold mb-2">Access unavailable</div>
          <div className="text-sm text-slate-600">{error}</div>
          <div className="text-xs text-slate-500 mt-4">Contact your HCD Legal contact for a new link.</div>
        </div>
      </div>
    );
  }
  if (!data) return <div className="p-8 text-slate-500">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="vendor-portal-page">
      <header className="bg-[#005289] text-white">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-white/10 flex items-center justify-center font-bold text-sm">HCD</div>
          <div>
            <div className="text-lg font-bold leading-tight">Health Care District of Palm Beach County</div>
            <div className="text-xs text-blue-100">Vendor Contract Portal · Read-only access</div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-6">
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Contract</div>
              <div className="text-xl font-bold text-slate-900 font-mono">{data.contract_number}</div>
              <div className="text-sm text-slate-600 mt-1">{data.vendor_legal_name}{data.vendor_dba ? ` (${data.vendor_dba})` : ""}</div>
              <div className="text-xs text-slate-500 mt-1">{data.description}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Current Status</div>
              <div className="inline-block mt-1 px-3 py-1 rounded-full text-xs font-bold text-white" style={{ background: "#005289" }}>
                {data.stage_label}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-100 text-sm">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Department</div>
              <div className="text-slate-900">{data.department}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Type</div>
              <div className="text-slate-900">{data.contract_type}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Timeline</div>
              <div className="text-slate-900">{data.timeline}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Received</div>
              <div className="text-slate-900">{data.initial_received_date ? new Date(data.initial_received_date).toLocaleDateString() : "—"}</div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Contract Documents</div>
          <div className="divide-y divide-slate-100">
            {data.vendor_documents.length === 0 && <div className="text-sm text-slate-500 py-3">No documents shared yet.</div>}
            {data.vendor_documents.map(d => (
              <div key={d.id} className="flex items-center gap-3 py-2.5">
                <FileText className="w-4 h-4 text-slate-500" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{d.filename}</div>
                  <div className="text-[11px] text-slate-500">{d.kind} · uploaded by {d.uploaded_by}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="vendor-upload-card">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Upload a revision</div>
          <p className="text-sm text-slate-600 mb-3">Attach your redlined contract or supporting documents. HCD Legal will be notified.</p>
          <div className="space-y-3">
            <label className="inline-flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-md text-xs bg-slate-50 cursor-pointer">
              <Upload className="w-3.5 h-3.5" />
              {file ? file.name : "Choose file..."}
              <input type="file" className="hidden" onChange={e => setFile(e.target.files[0])} data-testid="vendor-file" />
            </label>
            <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Optional note to HCD Legal..."
                      className="w-full min-h-[80px] p-2 text-sm border border-slate-200 rounded-md" data-testid="vendor-note" />
            <button onClick={upload} disabled={busy || !file}
                    className="h-9 px-4 rounded-md text-white text-sm font-semibold disabled:opacity-60"
                    style={{ background: "#00AE42" }} data-testid="vendor-submit">
              {busy ? "Uploading..." : "Send to HCD Legal"}
            </button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md" data-testid="vendor-timeline-card">
          <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#005289]" />
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Contract Timeline</div>
            <span className="ml-auto text-[10px] text-slate-500">{timeline?.count || 0} events</span>
          </div>
          <div className="p-4 max-h-96 overflow-y-auto">
            {!timeline && <div className="text-xs text-slate-500 text-center py-4">Loading timeline...</div>}
            {timeline && timeline.events.length === 0 && <div className="text-xs text-slate-500 text-center py-4">No events yet.</div>}
            {timeline && timeline.events.length > 0 && (
              <ol className="relative border-l-2 border-slate-200 ml-3" data-testid="vendor-timeline-list">
                {timeline.events.map((e, i) => {
                  const catColor = {
                    stage: "#00AE42", document: "#7C3AED", message: "#DB2777",
                    correspondence: "#0891B2", note: "#F59E0B", signature: "#059669",
                  }[e.category] || "#005289";
                  return (
                    <li key={i} className="mb-4 ml-6">
                      <span className="absolute -left-[9px] w-4 h-4 rounded-full border-2 border-white shadow" style={{ background: catColor }} />
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: catColor }}>{e.category}</span>
                        {e.actor && <span className="text-xs font-semibold text-slate-800">{e.actor}</span>}
                        <span className="text-[10px] text-slate-500">{new Date(e.at).toLocaleString()}</span>
                      </div>
                      <div className="text-sm text-slate-800 mt-0.5">{e.title}</div>
                      {e.detail && <div className="text-[12px] text-slate-600 whitespace-pre-wrap mt-0.5">{e.detail}</div>}
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md" data-testid="vendor-chat-card">
          <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-[#005289]" />
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Messages with HCD Legal</div>
          </div>
          <div className="p-4 max-h-72 overflow-y-auto space-y-2">
            {messages.length === 0 && <div className="text-sm text-slate-500 text-center py-3">No messages yet. Start the conversation below.</div>}
            {messages.map(m => (
              <div key={m.id} className={`flex ${m.from === "vendor" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 ${m.from === "vendor" ? "bg-[#005289] text-white" : "bg-slate-100 text-slate-900"}`}>
                  <div className="text-[10px] font-bold opacity-80 mb-0.5">{m.author_name}</div>
                  <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                  <div className="text-[10px] opacity-70 mt-1">{new Date(m.at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-slate-100 flex gap-2">
            <input value={chatBody} onChange={e => setChatBody(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && sendChat()}
                  placeholder="Reply to HCD Legal..."
                  className="flex-1 h-9 px-2 text-sm border border-slate-200 rounded-md" data-testid="vendor-chat-input" />
            <button onClick={sendChat}
                    className="h-9 px-3 rounded-md text-white text-xs font-semibold flex items-center gap-1"
                    style={{ background: "#005289" }} data-testid="vendor-chat-send">
              <Send className="w-3.5 h-3.5" /> Send
            </button>
          </div>
        </div>

        <div className="text-center text-xs text-slate-500">
          <Clock className="w-3 h-3 inline mr-1" />
          Link expires {new Date(data.expires_at).toLocaleDateString()} · This is a secure read-only portal.
        </div>
      </main>
    </div>
  );
}
